// HeaderNav.jsx
import React, { memo, useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HomeIcon,
  EyeIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  BellIcon,
  Bars3Icon,
  ChevronDownIcon,
  NewspaperIcon,
  ClipboardDocumentListIcon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../context/ThemeContext';
import UserDropdown from './UserDropdown';
import AuraLogo from './AuraLogo';
import AuraHelpAssistant from './common/AuraHelpAssistant';
import NotificationCenter from './NotificationCenter';
import axios from 'axios';

const HeaderNav = ({
  activeSection,
  onSectionChange,
  user,
  onLogout,
  visionCount = 0,
}) => {
  const [showHelpAssistant, setShowHelpAssistant] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dueReminderCount, setDueReminderCount] = useState(0);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const response = await axios.get('/api/platform/notifications/unread-count');
        setUnreadCount(response.data.data?.unread_count || 0);
      } catch (err) {
        console.error('Error loading unread count:', err);
      }
    };

    const loadDueReminders = async () => {
      try {
        const response = await axios.get('/api/engagement/reminders/due-count');
        setDueReminderCount(response.data?.count || 0);
      } catch (err) {
        setDueReminderCount(0);
      }
    };

    if (user) {
      loadUnreadCount();
      loadDueReminders();
      const interval = setInterval(() => {
        loadUnreadCount();
        loadDueReminders();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const primarySections = [
    { id: 'dashboard', label: 'Dashboard', icon: HomeIcon, to: '/dashboard' },
    { id: 'my-plan', label: 'My plan', icon: ClipboardDocumentListIcon, to: '/my-plan', badge: dueReminderCount },
    { id: 'community', label: 'Community', icon: UserGroupIcon, to: '/community' },
    { id: 'visions', label: 'Visions', icon: EyeIcon, to: '/visions', badge: visionCount },
  ];

  const moreSections = [
    { id: 'insights', label: 'News & actions', icon: NewspaperIcon, to: '/dashboard/insights' },
    { id: 'learn-and-play', label: 'Learn & Play', icon: AcademicCapIcon, to: '/learn-and-play' },
    { id: 'advisors', label: 'Find an Advisor', icon: UserGroupIcon, to: '/advisors' },
    { id: 'cashflow', label: 'Aura Cashflow', icon: CurrencyDollarIcon, to: '/cashflow' },
    { id: 'notes', label: 'Notes', icon: DocumentTextIcon, to: '/notes' },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMoreMenu && !event.target.closest('.more-menu-container')) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreMenu]);

  const navLinkClass = ({ isActive }) =>
    `relative flex items-center gap-2 px-3 py-2 text-body font-medium transition-colors duration-300 ${
      isActive ? 'nav-tab-active' : 'nav-tab'
    }`;

  return (
    <>
      <header
        data-tour="aura-header"
        className="sticky top-0 z-50 bg-bone border-b border-hairline"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="min-w-[200px]">
              <div
                onClick={() => setShowHelpAssistant(!showHelpAssistant)}
                className="cursor-pointer"
                title="Help"
              >
                <AuraLogo showText size="default" />
              </div>
            </div>

            <nav data-tour="aura-nav" className="flex items-center gap-1">
              {primarySections.map((section) => {
                const Icon = section.icon;
                return (
                  <NavLink key={section.id} to={section.to} className={navLinkClass}>
                    {({ isActive }) => (
                      <>
                        <Icon className={`w-5 h-5 ${isActive ? 'text-pine' : 'text-ink-muted'}`} />
                        <span>{section.label}</span>
                        {section.badge > 0 && (
                          <span className="ml-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-pine text-primary text-label font-medium">
                            {section.badge > 9 ? '9+' : section.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}

              <div className="relative more-menu-container ml-1">
                <button
                  type="button"
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                  className={`flex items-center gap-2 px-3 py-2 text-body font-medium transition-colors duration-300 ${
                    showMoreMenu ? 'nav-tab-active' : 'nav-tab'
                  }`}
                >
                  <Bars3Icon className="w-5 h-5" />
                  <span>More</span>
                  <ChevronDownIcon
                    className={`w-4 h-4 transition-transform duration-300 ${showMoreMenu ? 'rotate-180' : ''}`}
                  />
                </button>

                <AnimatePresence>
                  {showMoreMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                      className="absolute top-full left-0 mt-2 w-56 bg-bone-raised border border-hairline rounded-lg overflow-hidden z-50"
                    >
                      <div className="py-1 hairline-list">
                        {moreSections.map((section) => {
                          const Icon = section.icon;
                          return (
                            <NavLink
                              key={section.id}
                              to={section.to}
                              onClick={() => setShowMoreMenu(false)}
                              className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 text-body transition-colors duration-300 ${
                                  isActive
                                    ? 'bg-pine-tint text-pine'
                                    : 'text-ink-muted hover:bg-pine-tint hover:text-ink'
                                }`
                              }
                            >
                              {({ isActive }) => (
                                <>
                                  <Icon className={`w-5 h-5 ${isActive ? 'text-pine' : ''}`} />
                                  <span>{section.label}</span>
                                </>
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>

            <div className="min-w-[200px] flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={toggleTheme}
                className="p-2 text-ink-muted hover:text-ink transition-colors duration-300"
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
              </button>
              {user && (
                <button
                  type="button"
                  onClick={() => setShowNotifications(true)}
                  className="relative p-2 text-ink-muted hover:text-ink transition-colors duration-300"
                  aria-label="Notifications"
                >
                  <BellIcon className="w-6 h-6" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-danger text-primary text-label font-medium">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              )}
              <UserDropdown user={user} onLogout={onLogout} />
            </div>
          </div>
        </div>
      </header>

      <AuraHelpAssistant
        isOpen={showHelpAssistant}
        onClose={() => setShowHelpAssistant(false)}
        user={user}
      />

      {user && (
        <NotificationCenter
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          user={user}
        />
      )}
    </>
  );
};

export default memo(HeaderNav);
